// Фискальные справочники (выделено из reference.api.ts планом 108, Ф4).
import { api } from '@/lib/api';

import { defined, type ListParams } from './_shared.js';

// --- Фискальные справочники — fiscal-модуль ---------------------------------
// Юр. лица — единственный фискальный словарь. Налоговая ставка и система
// налогообложения теперь не отдельные справочники, а поля юр. лица (выбор из
// стандартного списка). Юр. лицо — богатая сущность с реквизитами (см. ниже).
// Ответы camelCase ({ items } / { item }); тела запросов snake_case.

// Юридические лица — богатый словарь (name = «Сокращённое название», ярлык в
// списках/дропдаунах + необязательные реквизиты, включая налоговую ставку и
// систему налогообложения). Роуты /fiscal/legal-entities, контракт rich:
// { items } / { item } с camelCase-полями, тело запроса — snake_case
// (full_name, legal_address, settlement_account, tax_rate, taxation_system…).

export interface LegalEntity {
  id: string;
  /** «Сокращённое название» — обязательный ярлык в списках/дропдаунах. */
  name: string;
  fullName: string | null;
  legalAddress: string | null;
  inn: string | null;
  ogrn: string | null;
  settlementAccount: string | null;
  bank: string | null;
  bik: string | null;
  corrAccount: string | null;
  /** Налоговая ставка (выбор из стандартного списка). */
  taxRate: string | null;
  /** Система налогообложения (выбор из стандартного списка). */
  taxationSystem: string | null;
  sortOrder: number;
  isArchived: boolean;
  /** Число активных шаблонов абонементов на юр.лице. */
  templatesCount: number;
}

export interface CreateLegalEntityBody {
  name: string;
  fullName?: string | null;
  legalAddress?: string | null;
  inn?: string | null;
  ogrn?: string | null;
  settlementAccount?: string | null;
  bank?: string | null;
  bik?: string | null;
  corrAccount?: string | null;
  taxRate?: string | null;
  taxationSystem?: string | null;
  sortOrder?: number;
}

export interface UpdateLegalEntityBody extends Partial<CreateLegalEntityBody> {
  isArchived?: boolean;
}

function legalEntityPayload(
  body: CreateLegalEntityBody | UpdateLegalEntityBody,
): Record<string, unknown> {
  return defined({
    name: body.name,
    full_name: body.fullName,
    legal_address: body.legalAddress,
    inn: body.inn,
    ogrn: body.ogrn,
    settlement_account: body.settlementAccount,
    bank: body.bank,
    bik: body.bik,
    corr_account: body.corrAccount,
    tax_rate: body.taxRate,
    taxation_system: body.taxationSystem,
    sort_order: body.sortOrder,
    is_archived: (body as UpdateLegalEntityBody).isArchived,
  });
}

export function listLegalEntities(params: ListParams = {}): Promise<{ items: LegalEntity[] }> {
  const sp = new URLSearchParams();
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: LegalEntity[] }>(`/fiscal/legal-entities${qs ? `?${qs}` : ''}`);
}

export function createLegalEntity(body: CreateLegalEntityBody): Promise<{ item: LegalEntity }> {
  return api.post('/fiscal/legal-entities', legalEntityPayload(body));
}

export function updateLegalEntity(
  id: string,
  body: UpdateLegalEntityBody,
): Promise<{ item: LegalEntity }> {
  return api.patch(`/fiscal/legal-entities/${id}`, legalEntityPayload(body));
}

export function archiveLegalEntity(id: string): Promise<void> {
  return api.delete<void>(`/fiscal/legal-entities/${id}`);
}

// catalog-эндпоинты принимают includeArchived (camelCase query) — сохраняем как было.
